import type { QueryClient } from '@tanstack/react-query';
import { isApiError, newClientId, sendMessage, uploadAttachment, type ApiError } from '../../api';
import {
  conversationKey,
  invalidateConversationLists,
  isReplyWindowClosed,
  messagesKey,
  recordWindowRefusal,
  refusalText,
} from '../conversation';
import { addMessageToCache, flattenMessages, type MessagePages } from './messages-cache';
import {
  appendLocal,
  patchLocal,
  readOutbox,
  removeLocal,
  type LocalAttachment,
  type LocalSend,
} from './outbox';
import type { PendingFile } from './attachments';
import { prependToDraft, readDraft, writeDraft } from './use-draft';

export { newClientId };

/** Erros em que o servidor RESPONDEU que não vai enviar: a mensagem com certeza não foi criada. */
const isRefusal = (error: unknown): error is ApiError =>
  isApiError(error) && error.status >= 400 && error.status < 500;

/** Texto para a pessoa quando o servidor recusa o envio (o texto cru do servidor nem sempre serve). */
export function refusalMessage(error: ApiError): string {
  switch (error.code) {
    case 'not_assigned':
      return 'Você não é mais o responsável por esta conversa. A mensagem não foi enviada.';
    case 'client_id_reused':
      return 'Não foi possível enviar: este envio entrou em conflito com outro. Edite e envie de novo.';
    case 'empty_text':
      return 'A mensagem está vazia.';
    case 'text_too_long':
    case 'caption_too_long':
    case 'attachment_too_large':
      return error.message;
    case 'attachment_type_not_allowed':
      return 'Este tipo de arquivo não pode ser enviado pelo WhatsApp.';
    case 'attachment_invalid':
      return 'O conteúdo do arquivo não corresponde ao tipo dele. Escolha outro arquivo.';
    case 'attachments_not_supported':
      return 'O serviço de mensagens conectado não aceita anexos.';
    case 'caption_not_supported':
      return 'Áudio não aceita legenda. Envie o texto em outra mensagem.';
    case 'voice_not_mono':
      return 'A mensagem de voz do WhatsApp precisa ser gravada em mono. Tente de novo com outro microfone.';
    case 'voice_not_opus':
    case 'voice_unreadable':
    case 'voice_empty':
      return 'Não foi possível usar a gravação. Grave de novo.';
    case 'voice_too_long':
      return 'A gravação é longa demais. Grave um áudio mais curto.';
    case 'storage_full':
      return 'O servidor está sem espaço para guardar o arquivo. Fale com quem administra o servidor.';
    case 'not_found':
      return 'Esta conversa não está mais disponível para você.';
    default:
      return error.message;
  }
}

/**
 * Pede de novo o que o envio muda no servidor: a lista de conversas (ordem, prévia) e a própria
 * conversa (responder zera as não lidas).
 */
function refreshAfterSend(queryClient: QueryClient, conversationId: string): void {
  void invalidateConversationLists(queryClient);
  void queryClient.invalidateQueries({ queryKey: conversationKey(conversationId) });
}

/**
 * O servidor recusou o envio porque a janela de resposta fechou entre a tela e o pedido (409
 * `reply_window_closed`): nada foi gravado nem enviado. Em vez de deixar uma bolha de "recusada"
 * numa conversa em que a pessoa nem pode mais escrever, o envio SAI da tela e o texto volta ao
 * rascunho (à frente do que ela já tenha digitado depois); a tela passa a mostrar o aviso de janela
 * fechada, com a explicação do servidor, e a conversa é pedida de novo para trazer o prazo verdadeiro.
 *
 * Os envios que esperavam a vez saem junto, na ordem: o servidor recusaria todos, e devolvê-los um a
 * um inverteria os textos no rascunho.
 */
function giveBackAfterWindowClosed(
  queryClient: QueryClient,
  conversationId: string,
  local: LocalSend,
  error: ApiError,
): void {
  const queue = readOutbox(queryClient, conversationId);
  // A fila some no logout/troca de conta (a resposta atrasada de outra sessão não escreve nada) e a
  // pessoa pode já ter descartado este envio.
  if (!queue.some((l) => l.clientId === local.clientId)) return;
  const giving = queue.filter((l) => l.clientId === local.clientId || l.state === 'queued');
  for (const item of giving) removeLocal(queryClient, conversationId, item.clientId);
  writeDraft(
    queryClient,
    conversationId,
    prependToDraft(
      giving.map((item) => item.text).join('\n\n'),
      readDraft(queryClient, conversationId),
    ),
  );
  recordWindowRefusal(queryClient, conversationId, {
    message: refusalText(error),
    draftKept: true,
  });
  void queryClient.invalidateQueries({ queryKey: conversationKey(conversationId) });
  void invalidateConversationLists(queryClient);
  // Num "Tentar de novo" a tentativa anterior (sem resposta) pode ter chegado antes de a janela
  // fechar: o histórico verdadeiro vem do servidor, e a pessoa vê a mensagem se ela existir.
  void queryClient.invalidateQueries({ queryKey: messagesKey(conversationId), exact: true });
}

/** `createdAt` da mensagem mais recente que o cache tem (relógio do servidor), ou null. */
function newestServerTime(queryClient: QueryClient, conversationId: string): string | null {
  const messages = flattenMessages(
    queryClient.getQueryData<MessagePages>(messagesKey(conversationId)),
  );
  return messages.at(-1)?.createdAt ?? null;
}

/**
 * Registra o envio na fila da conversa e começa a transmitir se for a vez dele. O texto já deve
 * vir aparado e validado. A mensagem aparece na tela na hora (é lida do cache pela lista).
 */
export function submitMessage(
  queryClient: QueryClient,
  conversationId: string,
  text: string,
  file?: Pick<PendingFile, 'file' | 'kind' | 'mimeType' | 'previewUrl'>,
  options: { voice?: boolean } = {},
): LocalSend {
  const attachment: LocalAttachment | undefined = file
    ? {
        file: file.file,
        kind: file.kind,
        mimeType: file.mimeType,
        previewUrl: file.previewUrl,
        attachmentId: null,
        progress: 0,
        ...(options.voice ? { voice: true } : {}),
      }
    : undefined;
  const local: LocalSend = {
    clientId: newClientId(),
    text,
    ...(attachment ? { attachment } : {}),
    createdAt: new Date().toISOString(),
    state: 'queued',
    attemptedAt: Date.now(),
    after: newestServerTime(queryClient, conversationId),
    error: null,
    retryable: false,
  };
  appendLocal(queryClient, conversationId, local);
  pumpOutbox(queryClient, conversationId);
  return local;
}

/**
 * Os envios de uma conversa saem um de cada vez, na ordem da fila: dois pedidos em paralelo
 * podem chegar ao servidor trocados, e a conversa do cliente sairia fora de ordem. Chamar à toa é
 * inofensivo.
 */
export function pumpOutbox(queryClient: QueryClient, conversationId: string): void {
  const list = readOutbox(queryClient, conversationId);
  if (list.some((l) => l.state === 'sending')) return;
  const next = list.find((l) => l.state === 'queued');
  if (!next) return;
  patchLocal(queryClient, conversationId, next.clientId, {
    state: 'sending',
    attemptedAt: Date.now(),
  });
  void transmit(queryClient, conversationId, next);
}

/** O envio local de agora (o estado muda enquanto o arquivo sobe). */
const currentLocal = (queryClient: QueryClient, conversationId: string, clientId: string) =>
  readOutbox(queryClient, conversationId).find((l) => l.clientId === clientId);

/**
 * Sobe o arquivo do envio (se ainda não subiu) e devolve o id dele no servidor. O progresso vai para a
 * bolha, de 1 em 1 %. Falhar aqui não criou mensagem nenhuma: repetir é sempre seguro.
 */
async function ensureUploaded(
  queryClient: QueryClient,
  conversationId: string,
  local: LocalSend,
): Promise<string | undefined> {
  const attachment = local.attachment;
  if (!attachment) return undefined;
  if (attachment.attachmentId) return attachment.attachmentId;
  let shown = -1;
  const uploaded = await uploadAttachment({
    conversationId,
    file: attachment.file,
    mimeType: attachment.mimeType,
    ...(attachment.voice ? { voice: true } : {}),
    onProgress: (fraction) => {
      const percent = Math.floor(fraction * 100);
      if (percent === shown) return;
      shown = percent;
      const now = currentLocal(queryClient, conversationId, local.clientId)?.attachment;
      if (now) {
        patchLocal(queryClient, conversationId, local.clientId, {
          attachment: { ...now, progress: fraction },
        });
      }
    },
  });
  const now = currentLocal(queryClient, conversationId, local.clientId)?.attachment;
  if (now) {
    patchLocal(queryClient, conversationId, local.clientId, {
      attachment: { ...now, attachmentId: uploaded.id, progress: 1 },
    });
  }
  return uploaded.id;
}

async function transmit(
  queryClient: QueryClient,
  conversationId: string,
  local: LocalSend,
): Promise<void> {
  // Enquanto o arquivo sobe nada foi criado no servidor: uma falha aqui é sempre segura de repetir.
  let uploading = true;
  try {
    const attachmentId = await ensureUploaded(queryClient, conversationId, local);
    uploading = false;
    const message = await sendMessage({
      conversationId,
      text: local.text,
      clientId: local.clientId,
      ...(attachmentId ? { attachmentId } : {}),
    });
    // Uma busca de mensagens já em voo (refetch ao focar a janela) pode ter lido o banco ANTES deste
    // envio; se terminasse depois da escrita abaixo, sobrescreveria o cache e a mensagem sumiria da
    // tela. Cancelada, o cache mantém o que existia e a escrita vale.
    await queryClient.cancelQueries({ queryKey: messagesKey(conversationId), exact: true });
    // A mensagem do servidor entra no cache ANTES de a local sair (e sem duplicar se um refetch já
    // a tiver trazido): nenhum quadro mostra a conversa sem ela.
    const cached = addMessageToCache(queryClient, conversationId, message);
    removeLocal(queryClient, conversationId, local.clientId);
    // Cache de mensagens vazio (a lista ainda nem carregou): a busca em curso pode ter começado
    // antes do envio, então pede-se de novo.
    if (!cached) void queryClient.invalidateQueries({ queryKey: messagesKey(conversationId) });
    refreshAfterSend(queryClient, conversationId);
  } catch (error) {
    if (uploading && !isReplyWindowClosed(error)) {
      patchLocal(queryClient, conversationId, local.clientId, uploadFailure(error));
      if (isApiError(error) && (error.code === 'not_assigned' || error.code === 'not_found')) {
        void queryClient.invalidateQueries({ queryKey: conversationKey(conversationId) });
        void invalidateConversationLists(queryClient);
      }
    } else if (isReplyWindowClosed(error)) {
      giveBackAfterWindowClosed(queryClient, conversationId, local, error);
    } else if (isRefusal(error)) {
      const lostFile = error.code === 'attachment_not_found';
      const now = currentLocal(queryClient, conversationId, local.clientId)?.attachment;
      patchLocal(queryClient, conversationId, local.clientId, {
        state: 'rejected',
        error: lostFile
          ? 'O arquivo não está mais no servidor. Tente de novo para enviá-lo outra vez.'
          : refusalMessage(error),
        // 429 e CSRF recusam ANTES de tratar o pedido: tentar de novo mais tarde é seguro. Arquivo que
        // sumiu do servidor também: a nova tentativa sobe o arquivo outra vez.
        retryable:
          lostFile || error.status === 429 || error.status === 408 || error.code === 'invalid_csrf',
        ...(lostFile && now ? { attachment: { ...now, attachmentId: null, progress: 0 } } : {}),
      });
      if (error.code === 'not_assigned' || error.code === 'not_found') {
        // A permissão mudou (`canWrite`) ou a conversa saiu da visão: a tela precisa saber.
        void queryClient.invalidateQueries({ queryKey: conversationKey(conversationId) });
        void invalidateConversationLists(queryClient);
      }
    } else {
      // Rede, tempo limite, 5xx ou resposta ilegível: pode ter chegado. O mesmo clientId deixa a
      // pessoa tentar de novo sem risco de duplicar.
      patchLocal(queryClient, conversationId, local.clientId, {
        state: 'unconfirmed',
        error: null,
      });
      // Se a mensagem CHEGOU (só a resposta se perdeu), a busca a traz e o envio local se resolve
      // sozinho. Sem isto a pessoa poderia descartar e reescrever o texto com outro clientId, e o
      // cliente receberia a mensagem duas vezes.
      void queryClient.invalidateQueries({ queryKey: messagesKey(conversationId), exact: true });
    }
  } finally {
    pumpOutbox(queryClient, conversationId);
  }
}

/** Texto e estado de um envio que falhou ENQUANTO O ARQUIVO SUBIA (nada foi criado no servidor). */
function uploadFailure(error: unknown): Partial<LocalSend> {
  if (isRefusal(error) && error.status !== 408 && error.status !== 429) {
    // O servidor respondeu que não aceita este arquivo (tipo, tamanho, conteúdo, permissão).
    return {
      state: 'rejected',
      error: refusalMessage(error),
      retryable: error.code === 'invalid_csrf',
    };
  }
  // Rede, tempo esgotado, 5xx: o arquivo não chegou inteiro, e nada foi enviado ao cliente.
  return {
    state: 'rejected',
    error: 'Não foi possível enviar o arquivo. Verifique a conexão e tente de novo.',
    retryable: true,
  };
}

/** "Tentar de novo": volta o envio para a fila com o MESMO clientId. */
export function retryLocal(
  queryClient: QueryClient,
  conversationId: string,
  clientId: string,
): void {
  patchLocal(queryClient, conversationId, clientId, {
    state: 'queued',
    attemptedAt: Date.now(),
    error: null,
    retryable: false,
  });
  pumpOutbox(queryClient, conversationId);
}

/** Tira o envio da tela. Quem chama decide o que fazer com o texto (devolver ao compositor). */
export function discardLocal(
  queryClient: QueryClient,
  conversationId: string,
  clientId: string,
): void {
  removeLocal(queryClient, conversationId, clientId);
}
